import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// AppCrashError has minimal dependencies so that it is as stable as possible and usable in any context.
// Because of this, it intentionally duplicates styles from wolt-cookbook and re-implements functionality found
// elsewhere in the same package and repository.
import { styled } from 'styled-components';
const Root = styled.div `
  position: fixed;
  background-color: var(--cb-color-bg, rgba(255, 255, 255, 1)); /* salt100 */
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  color: var(--cb-color-text, rgba(32, 33, 37, 1)); /* pepper100 */
`;
const Overlay = styled.div `
  position: absolute;
  inset: 0;
  background-color: var(--cb-color-bg-overlay, #00000052); /* space32 */
`;
const Container = styled.div.attrs({ className: 'cb-elevated' }) `
  position: relative;
  padding: 32px;
  border-radius: 8px;
  background-color: var(--cb-color-bg, rgba(255, 255, 255, 1)); /* salt100 */

  box-shadow: var(
    --cb-shadow-large,
    0px 8px 28px 0px #0000001f,
    0px 2px 6px 0px #0000001f,
    0px 0px 1px 0px #0000001f
  ); /* elevation large */

  text-align: left;

  max-width: 768px;

  font-feature-settings: 'kern';
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    Roboto,
    'Open Sans',
    'Helvetica Neue',
    sans-serif;
  font-size: 14px;
  line-height: 20px;

  pointer-events: all;
`;
const Header = styled.header `
  margin-bottom: 24px;
`;
const Heading = styled.h2 `
  margin: 0;
  color: rgba(249, 58, 37, 1); /* strawberry100 */

  font-feature-settings: 'kern';
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    Roboto,
    'Open Sans',
    'Helvetica Neue',
    sans-serif;
  font-size: 24px;
  line-height: 28px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
`;
const Content = styled.section ``;
const DescriptionText = styled.div `
  & + & {
    margin-top: 16px;
  }
`;
const SlackLink = styled.a `
  text-decoration: none;
  font-weight: 500;
  color: var(--cb-color-text-brand, rgba(0, 157, 224, 1)); /* wolt100 */
  background: transparent;
  &:hover {
    color: var(--cb-color-text-brand-hovered, rgba(0, 157, 224, 0.92)); /* wolt92 */
  }
  &:active {
    color: var(--cb-color-text-brand-pressed, rgba(0, 157, 224, 0.8)); /* wolt80 */
  }
`;
const DebugInfo = styled.pre `
  overflow: auto;
  background-color: var(--cb-color-bg-surface-secondary, rgba(32, 33, 37, 0.04)); /* pepper4 */
  padding: 16px 32px;
  margin: 16px -32px 0;
  max-height: 40vh;
`;
const Footer = styled.footer `
  margin-top: 25px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const SecondaryDebugId = styled.span `
  font-feature-settings: 'kern';
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    Roboto,
    'Open Sans',
    'Helvetica Neue',
    sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: var(--cb-color-text-subdued, rgba(32, 33, 37, 0.64)); /* pepper64 */
  opacity: 0.5;
`;
const ActionsContainer = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;
const CopyToClipboardButton = styled.button `
  display: flex;
  align-items: center;
  padding: 0;
  appearance: none;
  border: none;
  font-feature-settings: 'kern';
  text-rendering: optimizelegibility;
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    Roboto,
    'Open Sans',
    'Helvetica Neue',
    sans-serif;
  font-size: inherit;
  line-height: inherit;
  font-weight: 500;
  font-weight: 500;
  color: var(--cb-color-text-brand, rgba(0, 157, 224, 1)); /* wolt100 */
  background: transparent;
  cursor: pointer;
  svg {
    width: 12px;
    margin-inline: 0px 8px;
    fill: currentColor;
  }
  &:hover {
    color: var(--cb-color-text-brand-hovered, rgba(0, 157, 224, 0.92)); /* wolt92 */
  }
  &:active {
    color: var(--cb-color-text-brand-pressed, rgba(0, 157, 224, 0.8)); /* wolt80 */
  }
`;
const ReloadButton = styled.button `
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-feature-settings: 'kern';
  text-rendering: optimizelegibility;
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    Roboto,
    'Open Sans',
    'Helvetica Neue',
    sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-style: normal;
  font-stretch: normal;
  font-weight: 500;
  color: var(--cb-color-text-brand, rgba(0, 157, 224, 1)); /* wolt100 */
  background-color: var(--cb-color-bg-surface-brand, rgba(0, 157, 224, 0.08)); /* wolt8 */
  appearance: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  svg {
    width: 12px;
    fill: currentColor;
    margin-inline: 0px 8px;
  }
  &:hover {
    background-color: var(
      --cb-color-bg-surface-brand-hovered,
      rgba(0, 157, 224, 0.12)
    ); /* wolt12 */
  }
  &:active {
    background-color: var(
      --cb-color-bg-surface-brand-pressed,
      rgba(0, 157, 224, 0.24)
    ); /* wolt24 */
  }
`;
export const AppCrashError = (props) => {
    const error = props.error?.stack ?? props.error?.toString();
    const debugInfo = `
UUID: ${props.errorId}
Error link: ${props.errorUrl}
URL: ${window.location.href}
${error?.replaceAll(window.location.origin, '') ?? ''}
ComponentStack: ${props.info?.componentStack.replaceAll(window.location.origin, '') ?? 'N/A'}
`.trim();
    const debugInfoMd = `\`\`\`\n${debugInfo}\n\`\`\``;
    return (_jsxs(Root, { children: [_jsx(Overlay, {}), _jsxs(Container, { children: [_jsx(Header, { children: _jsx(Heading, { children: "Oops, there was an unexpected error!" }) }), _jsxs(Content, { children: [_jsx(DescriptionText, { children: "The developers have been notified about the issue automatically." }), _jsxs(DescriptionText, { children: ["However, if you need it to be fixed ASAP, please contact", ' ', _jsx(SlackLink, { href: "https://wolt.slack.com/archives/C04A7A44A", rel: "nofollow", children: "#tech-help" }), ' ', "in Slack. Please also attach error info in your message!"] }), _jsxs(ActionsContainer, { children: [_jsxs(CopyToClipboardButton, { title: "Copy to clipboard", onClick: () => {
                                            window.navigator.clipboard.writeText(debugInfoMd);
                                        }, children: [_jsx("svg", { viewBox: "0 0 24 25", children: _jsx("path", { d: "M11.937 5.472V3.484a.5.5 0 00-.5-.5h-.758a.251.251 0 01-.249-.222 1.989 1.989 0 00-3.953 0 .249.249 0 01-.249.222h-.755a.5.5 0 00-.5.5v1.988a.5.5 0 00.5.5h5.967a.5.5 0 00.497-.5zM8.59 21.382a1.244 1.244 0 01-.88 2.123H1.494A1.493 1.493 0 010 22.012V4.975A1.494 1.494 0 011.5 3.48h1.721a.25.25 0 01.25.247c.004.4.016 1.234.055 2a.25.25 0 01-.2.258A1.086 1.086 0 002.484 7.1v12.782a1.073 1.073 0 001 1.136H7.71c.33 0 .646.131.88.364zm4.86-15.485a.251.251 0 01-.05-.17c.04-.503.041-1.522.039-1.995a.25.25 0 01.25-.251h1.724a1.495 1.495 0 011.5 1.494V8.5a1.244 1.244 0 11-2.489 0V7.1a1.09 1.09 0 00-.824-1.108.251.251 0 01-.15-.095zM15.04 18.27h3.979a.75.75 0 110 1.5h-3.979a.75.75 0 010-1.5zm-.75-2.729a.75.75 0 00.75.749h2.486a.75.75 0 000-1.5h-2.486a.749.749 0 00-.75.751zm9.057-1.843c.1.242.152.501.152.763V23a2 2 0 01-2 2h-9a2 2 0 01-2-2v-9.945a1.993 1.993 0 011.994-1.988H20.1a1.994 1.994 0 011.41.584l1.406 1.4c.185.185.332.405.432.646zm-10.555-.344a1 1 0 00-.293.707l.005 7.94a1 1 0 001 1h7a1 1 0 001-1v-7.124a1 1 0 00-.293-.708l-.818-.816a1 1 0 00-.707-.292H13.5a1 1 0 00-.707.293z" }) }), "Click to copy error info for developers"] }), _jsx(SecondaryDebugId, { children: props.errorId })] }), _jsx(DebugInfo, { children: debugInfo })] }), _jsxs(Footer, { children: [_jsxs(ReloadButton, { onClick: () => window.location.reload(), children: [_jsx("svg", { viewBox: "0 0 24 24", children: _jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M13.9327 3.9769C15.4063 4.33458 16.7524 5.09227 17.8227 6.16641C18.89 7.23841 19.6429 8.58265 19.9992 10.0528C20.3555 11.523 20.3018 13.0627 19.8437 14.5044C19.832 14.5412 19.812 14.5749 19.7851 14.6026C19.7582 14.6304 19.7253 14.6516 19.6888 14.6645C19.6524 14.6773 19.6135 14.6816 19.5751 14.6769C19.5367 14.6722 19.5 14.6587 19.4677 14.6374L18.1287 13.7564C17.9635 13.6478 17.7698 13.5905 17.5721 13.5919C17.3744 13.5933 17.1816 13.6533 17.0179 13.7643C16.8543 13.8753 16.7272 14.0323 16.6528 14.2154C16.5783 14.3986 16.5599 14.5997 16.5997 14.7934L17.5137 19.1994C17.5598 19.4251 17.6824 19.628 17.8608 19.7738C18.0391 19.9195 18.2624 19.9992 18.4927 19.9994C18.5599 19.9992 18.627 19.9922 18.6927 19.9784L23.0997 19.0704C23.2938 19.0307 23.4717 18.9343 23.6109 18.7934C23.7501 18.6525 23.8443 18.4734 23.8815 18.2789C23.9188 18.0844 23.8975 17.8831 23.8203 17.7008C23.743 17.5184 23.6134 17.363 23.4477 17.2544L22.0477 16.3324C21.9998 16.301 21.9643 16.2539 21.9471 16.1992C21.93 16.1445 21.9323 16.0856 21.9537 16.0324C22.7202 14.1387 22.9312 12.0658 22.562 10.0565C22.1929 8.04714 21.2589 6.18459 19.8694 4.68693C18.4799 3.18927 16.6925 2.1185 14.7165 1.60003C12.7404 1.08156 10.6575 1.13684 8.71173 1.75941C8.39612 1.86019 8.13348 2.08222 7.98158 2.37666C7.82967 2.67109 7.80095 3.0138 7.90173 3.32941C8.00252 3.64502 8.22454 3.90767 8.51898 4.05957C8.81341 4.21147 9.15613 4.24019 9.47173 4.13941C10.9154 3.67547 12.4591 3.61923 13.9327 3.9769ZM0.375935 5.31227C0.240133 5.4559 0.149946 5.63656 0.116764 5.83143C0.0818157 6.02742 0.106161 6.22938 0.186686 6.41146C0.267212 6.59353 0.400249 6.74742 0.568764 6.85343L1.91476 7.69943C1.96374 7.72998 2.00053 7.77666 2.01878 7.83142C2.03704 7.88619 2.03561 7.9456 2.01476 7.99943C1.25888 9.88446 1.05245 11.9451 1.41938 13.9426C1.78631 15.9401 2.71158 17.7928 4.08804 19.2861C5.46449 20.7795 7.23582 21.8524 9.19688 22.3805C11.1579 22.9087 13.2285 22.8705 15.1688 22.2704C15.3295 22.2254 15.4796 22.1486 15.6101 22.0447C15.7407 21.9407 15.8491 21.8117 15.929 21.6651C16.0089 21.5186 16.0587 21.3575 16.0753 21.1914C16.092 21.0254 16.0752 20.8576 16.026 20.6981C15.9767 20.5387 15.8961 20.3906 15.7887 20.2628C15.6814 20.135 15.5495 20.03 15.4009 19.954C15.2523 19.878 15.09 19.8325 14.9236 19.8202C14.7571 19.8079 14.5899 19.829 14.4318 19.8824C12.9884 20.329 11.45 20.3705 9.98464 20.0025C8.51925 19.6345 7.18307 18.8711 6.1219 17.7956C5.06073 16.72 4.31533 15.3737 3.96703 13.9035C3.61872 12.4333 3.68089 10.8957 4.14676 9.45843C4.15854 9.42206 4.17843 9.38884 4.20494 9.36129C4.23144 9.33374 4.26387 9.31259 4.29976 9.29943C4.33572 9.28681 4.37411 9.28268 4.41193 9.28736C4.44975 9.29204 4.48597 9.30542 4.51776 9.32643L5.96076 10.2324C6.12821 10.3376 6.32309 10.3908 6.52077 10.3853C6.71845 10.3798 6.91005 10.3157 7.07135 10.2013C7.23265 10.0869 7.3564 9.92723 7.42696 9.74249C7.49752 9.55775 7.51172 9.35624 7.46776 9.16343L6.46776 4.77643C6.43851 4.64817 6.38424 4.52694 6.30806 4.41969C6.23188 4.31244 6.13528 4.22127 6.02381 4.15142C5.91234 4.08156 5.78818 4.03438 5.65844 4.01259C5.5287 3.9908 5.39594 3.99482 5.26776 4.02443L0.879763 5.02443C0.687063 5.06847 0.511738 5.16864 0.375935 5.31227Z" }) }), "Reload to continue"] }), _jsx(SecondaryDebugId, { children: props.errorId })] })] })] }));
};
//# sourceMappingURL=AppCrashError.js.map